/**
 * §A.6 · Las disciplinas de la plataforma.
 *
 * ## Son cuatro, y añadir una quinta es más que una fila
 *
 * El contrato lo dice: «una quinta disciplina es **una fila y su sistema de grados**;
 * sin plantilla de cinturones, los clubes que la enseñen no tienen de dónde clonar y
 * acaban inventándose uno cada uno».
 *
 * Así que al crear una se avisa de lo que falta después, y la pantalla enseña cuántas
 * plantillas tiene cada una: una disciplina con cero plantillas es una disciplina a
 * medio dar de alta.
 *
 * ## El `code` no se edita
 *
 * Enlaza la disciplina con sus sistemas de grados **y con el seed**. Se enseña como
 * dato y sin campo.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { cuenta } from '@/shared/ui/formato';
import type { DisciplinaGlobal } from '@/backoffice/tiposDeCatalogos';

export function CatalogoDeDisciplinas() {
  const cache = useQueryClient();
  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<DisciplinaGlobal | null>(null);
  const [code, setCode] = useState('');
  const [nombre, setNombre] = useState('');
  const [conPeso, setConPeso] = useState(false);
  const [problema, setProblema] = useState<Problema | null>(null);

  const disciplinas = useQuery({
    queryKey: ['admin', 'disciplinas'],
    queryFn: async () => {
      const { data, error } = await api.v1.admin.disciplines.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as DisciplinaGlobal[];
    },
  });

  const guardar = useMutation({
    mutationFn: async () => {
      if (editando) {
        const r = await api.v1.admin
          .disciplines({ id: editando.id })
          .patch({ name: nombre.trim(), has_weight_classes: conPeso });
        if (r.error) throw new ErrorDeApi(aProblema(r.error));
        return r.data;
      }
      const r = await api.v1.admin.disciplines.post({
        code: code.trim(),
        name: nombre.trim(),
        has_weight_classes: conPeso,
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setCreando(false);
      setEditando(null);
      setCode('');
      setNombre('');
      setConPeso(false);
      void cache.invalidateQueries({ queryKey: ['admin', 'disciplinas'] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (disciplinas.isPending) return <Cargando filas={4} etiqueta="Cargando" />;

  if (disciplinas.isError || !disciplinas.data) {
    return (
      <PanelError
        problema={aProblema(disciplinas.error)}
        reintentar={() => void disciplinas.refetch()}
      />
    );
  }

  if (creando || editando) {
    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          setProblema(null);
          guardar.mutate();
        }}
      >
        <div className="titulo-fila">
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setCreando(false);
              setEditando(null);
              setProblema(null);
            }}
          >
            ← Disciplinas
          </button>
        </div>

        <h1>{editando ? `Editar «${editando.name}»` : 'Una disciplina más'}</h1>

        {!editando && (
          <div className="aviso" role="status">
            <p>
              <strong>Esto es media alta.</strong> La otra mitad es su sistema
              de grados: sin una plantilla de cinturones, los clubes que la
              enseñen no tienen de dónde clonar y acaban inventándose uno cada
              uno.
            </p>
            <p className="tenue">
              Se crea en «Sistemas de grados», eligiendo esta disciplina.
            </p>
          </div>
        )}

        {editando && editando.academies > 0 && (
          <div className="aviso" role="status">
            {/* La cifra convierte «cuidado» en algo que se puede valorar. */}
            La enseñan {cuenta(editando.academies, 'club', 'clubes')}. Cambiarle
            el nombre se lo cambia a todos, y el código no se toca.
          </div>
        )}

        <div className="rejilla">
          {!editando && (
            <label>
              Código
              <input
                required
                maxLength={30}
                pattern="[a-z0-9_]+"
                placeholder="kendo"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
          )}

          <label>
            Cómo se llama
            <input
              required
              maxLength={60}
              placeholder="Kendo"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
          </label>

          <label>
            Compite por peso
            <select
              value={conPeso ? 'si' : 'no'}
              onChange={(e) => setConPeso(e.target.value === 'si')}
            >
              <option value="no">No</option>
              <option value="si">Sí, con categorías de peso</option>
            </select>
          </label>
        </div>

        <p className="tenue">
          El código no se puede cambiar después: es lo que enlaza la disciplina
          con sus sistemas de grados. En minúsculas y sin acentos.
        </p>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={guardar.isPending}>
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={guardar.isPending}
            onClick={() => {
              setCreando(false);
              setEditando(null);
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  return (
    <section>
      <div className="titulo-fila">
        <h1>Disciplinas</h1>
        <button type="button" onClick={() => setCreando(true)}>
          Una disciplina más
        </button>
      </div>

      <p className="tenue">
        Las que existen en toda la plataforma.{' '}
        <strong>Cada cambio de aquí lo ven los doscientos clubes.</strong>
      </p>

      {problema && <PanelError problema={problema} />}

      <div className="tabla-marco">
        <table className="tabla">
          <thead>
            <tr>
              <th>Disciplina</th>
              <th className="num">Clubes</th>
              <th className="num">Plantillas de grados</th>
              <th>Por peso</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {disciplinas.data.map((d) => (
              <tr key={d.id}>
                <td>
                  {d.name}
                  <br />
                  <code className="tenue">{d.code}</code>
                </td>
                <td className="num">{d.academies}</td>
                <td className="num">
                  {/*
                    Cero plantillas es una disciplina a medio dar de alta: los clubes
                    que la enseñen no tienen de dónde clonar.
                  */}
                  {d.schemes === 0 ? <strong>0</strong> : d.schemes}
                </td>
                <td className="tenue">{d.has_weight_classes ? 'Sí' : 'No'}</td>
                <td>
                  <button
                    type="button"
                    className="btn-plano"
                    onClick={() => {
                      setNombre(d.name);
                      setConPeso(d.has_weight_classes);
                      setEditando(d);
                      setProblema(null);
                    }}
                  >
                    Editar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {disciplinas.data.some((d) => d.schemes === 0) && (
        <p className="tenue">
          <strong>Hay disciplinas sin plantilla de grados.</strong> Un club que
          enseñe una de esas no tiene escalera que clonar, así que se inventará
          la suya y su pasaporte no se parecerá al de nadie.
        </p>
      )}
    </section>
  );
}
